import React, { useState, useEffect } from 'react';
import { Sparkles, Check, X, ShieldCheck, Zap, Activity, Heart, RefreshCw, AlertCircle, ExternalLink } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { getSubscriptionOfferings, purchaseSubscriptionPackage, restoreUserPurchases } from '../utils/purchases';

const API_URL = import.meta.env.VITE_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:3000/api' : '/api');

export default function PricingModal({ isOpen, onClose, userProfile, onSubscriptionSuccess }) {
  const [offerings, setOfferings] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [loadingOfferings, setLoadingOfferings] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setErrorMessage(null);
      setSuccessMessage(null);
      return;
    }

    let isMounted = true;
    async function loadOfferings() {
      setLoadingOfferings(true);
      setErrorMessage(null);
      try {
        const result = await getSubscriptionOfferings();
        if (isMounted) {
          const pkgs = result?.packages || [];
          setOfferings(pkgs);
          // Prefer annual package by default, otherwise first available
          const annualPkg = pkgs.find(p => p.packageType === 'ANNUAL' || p.identifier?.includes('annual'));
          setSelectedPackage(annualPkg || pkgs[0] || null);
        }
      } catch (err) {
        console.error('Error cargando planes:', err);
        if (isMounted) {
          setErrorMessage('No se pudieron cargar los planes de suscripción. Inténtalo de nuevo.');
        }
      } finally {
        if (isMounted) setLoadingOfferings(false);
      }
    }

    loadOfferings();

    return () => {
      isMounted = false;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleOpenLink = async (path) => {
    const fullUrl = path.startsWith('http') ? path : `${window.location.origin}${path.startsWith('/') ? '' : '/'}${path}`;
    if (Capacitor.isNativePlatform()) {
      try {
        await Browser.open({ url: fullUrl, windowName: '_blank' });
        return;
      } catch (e) {
        console.warn('Browser.open error:', e);
      }
    }
    window.open(fullUrl, '_blank', 'noopener,noreferrer');
  };

  const handlePurchase = async () => {
    if (!selectedPackage) return;
    setPurchasing(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const token = localStorage.getItem('token');
      const res = await purchaseSubscriptionPackage(selectedPackage, API_URL, token);

      if (res.cancelled) {
        setPurchasing(false);
        return;
      }

      if (res.success) {
        setSuccessMessage('¡Bienvenido a NousMetrics PRO! Tu cuenta ha sido actualizada.');
        if (onSubscriptionSuccess) {
          onSubscriptionSuccess();
        }
        setTimeout(() => {
          onClose();
        }, 1800);
      }
    } catch (err) {
      console.error('Error en compra:', err);
      setErrorMessage(err.message || 'Ocurrió un error al procesar el pago con la App Store.');
    } finally {
      setPurchasing(false);
    }
  };

  const handleRestore = async () => {
    setRestoring(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const token = localStorage.getItem('token');
      const res = await restoreUserPurchases(API_URL, token);

      if (res.isPro) {
        setSuccessMessage('¡Suscripción restaurada con éxito!');
        if (onSubscriptionSuccess) {
          onSubscriptionSuccess();
        }
        setTimeout(() => {
          onClose();
        }, 1800);
      } else {
        setErrorMessage('No se encontraron compras activas asociadas a este Apple ID.');
      }
    } catch (err) {
      console.error('Error al restaurar compras:', err);
      setErrorMessage(err.message || 'No se pudieron restaurar las compras en este momento.');
    } finally {
      setRestoring(false);
    }
  };

  const isAlreadyPro = userProfile?.subscriptionTier === 'PRO';

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 3000,
      background: 'rgba(5, 8, 16, 0.82)', backdropFilter: 'blur(12px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '1rem',
      animation: 'fadeIn 0.25s ease-out',
      overflowY: 'auto'
    }}>
      <div className="glass-panel" style={{
        width: '100%', maxWidth: '520px', maxHeight: '92vh',
        overflowY: 'auto', padding: '2rem 1.75rem',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        position: 'relative', textAlign: 'center',
        borderRadius: '24px',
        border: '1px solid rgba(0, 229, 255, 0.25)',
        boxShadow: '0 25px 60px rgba(0,0,0,0.7), 0 0 40px rgba(0, 229, 255, 0.15)'
      }}>
        {/* Close Button */}
        <button 
          onClick={onClose} 
          disabled={purchasing || restoring}
          aria-label="Cerrar"
          style={{
            position: 'absolute', top: '1.25rem', right: '1.25rem',
            background: 'rgba(255, 255, 255, 0.06)', border: 'none',
            color: 'var(--text-secondary)', cursor: 'pointer',
            padding: '0.4rem', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'background 0.2s'
          }}
          onMouseOver={e => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.12)'}
          onMouseOut={e => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.06)'}
        >
          <X size={20} />
        </button>

        {/* Top Badge */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
          padding: '0.35rem 0.9rem', borderRadius: '999px',
          background: 'linear-gradient(135deg, rgba(0, 229, 255, 0.15), rgba(139, 92, 246, 0.2))',
          border: '1px solid rgba(0, 229, 255, 0.4)',
          color: '#00e5ff', fontSize: '0.78rem', fontWeight: 800,
          letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '0.9rem'
        }}>
          <Sparkles size={14} style={{ color: '#00e5ff' }} />
          NOUSMETRICS PRO
        </div>

        {/* Main Title & Tagline */}
        <h2 style={{ fontSize: '1.65rem', fontWeight: 800, color: 'white', margin: '0 0 0.4rem 0', lineHeight: 1.25 }}>
          Desbloquea tu Máximo Potencial
        </h2>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: '0 0 1.25rem 0', lineHeight: 1.45, maxWidth: '420px' }}>
          Copiloto Fisiológico IA sin límites, curva de fatiga Coggan (CTL/ATL/TSB) y analítica clínica de rendimiento.
        </p>

        {/* Value Proposition Bento */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.65rem',
          width: '100%', marginBottom: '1.25rem', textAlign: 'left'
        }}>
          <FeatureCard 
            icon={<Zap size={18} color="#00e5ff" />}
            title="Copiloto IA Ilimitado"
            desc="Consultas y prescripción deportiva en tiempo real sin límites."
          />
          <FeatureCard 
            icon={<Activity size={18} color="#8b5cf6" />}
            title="Fatiga Coggan PMC"
            desc="Modelado de CTL, ATL y TSB con zonas de forma óptima."
          />
          <FeatureCard 
            icon={<Heart size={18} color="#ef4444" />}
            title="Readiness & HRV"
            desc="Score de predisposición con pasillo biológico de 28 días."
          />
          <FeatureCard 
            icon={<ShieldCheck size={18} color="#10b981" />}
            title="Umbrales Clínicos"
            desc="VT1, VT2 y potencia integrados desde tus pruebas médicas."
          />
        </div>

        {/* Already PRO banner */}
        {isAlreadyPro && (
          <div style={{
            background: 'rgba(16, 185, 129, 0.15)', border: '1px solid rgba(16, 185, 129, 0.35)',
            borderRadius: '12px', padding: '0.75rem 1rem', width: '100%', marginBottom: '1rem',
            color: '#10b981', fontSize: '0.88rem', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem'
          }}>
            <Check size={18} /> Ya tienes una suscripción PRO activa
          </div>
        )}

        {/* Package Selector (App Store compliant packages) */}
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1.25rem' }}>
          {loadingOfferings ? (
            <div style={{ padding: '1.5rem', color: 'var(--text-muted)', fontSize: '0.9rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
              <RefreshCw size={18} className="spin-animation" /> Cargando planes de la App Store...
            </div>
          ) : (
            offerings.map((pkg) => {
              const isAnnual = pkg.packageType === 'ANNUAL' || pkg.identifier?.toLowerCase().includes('annual');
              const isSelected = selectedPackage?.identifier === pkg.identifier;
              const priceStr = pkg.product?.priceString || (isAnnual ? '79,00 €' : '8,99 €');
              const title = isAnnual ? 'Suscripción Anual' : 'Suscripción Mensual';
              const subtitle = isAnnual ? '6,58 € / mes • Facturación anual de 79,00 €' : '8,99 € / mes • Facturación mensual';

              return (
                <div
                  key={pkg.identifier}
                  onClick={() => setSelectedPackage(pkg)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '0.95rem 1.15rem', borderRadius: '14px',
                    cursor: 'pointer', transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    background: isSelected 
                      ? 'linear-gradient(135deg, rgba(0, 229, 255, 0.12), rgba(139, 92, 246, 0.15))' 
                      : 'rgba(255, 255, 255, 0.03)',
                    border: isSelected ? '2px solid #00e5ff' : '1px solid rgba(255, 255, 255, 0.1)',
                    position: 'relative', textAlign: 'left',
                    boxShadow: isSelected ? '0 0 20px rgba(0, 229, 255, 0.2)' : 'none'
                  }}
                >
                  {isAnnual && (
                    <span style={{
                      position: 'absolute', top: '-10px', right: '1rem',
                      background: 'linear-gradient(135deg, #00e5ff, #8b5cf6)',
                      color: '#050810', fontSize: '0.68rem', fontWeight: 900,
                      padding: '0.2rem 0.6rem', borderRadius: '8px', letterSpacing: '0.04em',
                      boxShadow: '0 2px 8px rgba(0, 229, 255, 0.4)'
                    }}>
                      AHORRA 25%
                    </span>
                  )}

                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
                    <div style={{
                      width: '20px', height: '20px', borderRadius: '50%',
                      border: isSelected ? '6px solid #00e5ff' : '2px solid var(--text-muted)',
                      background: isSelected ? 'white' : 'transparent',
                      transition: 'all 0.2s', flexShrink: 0
                    }} />
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '0.98rem', color: 'white' }}>
                        {title}
                      </div>
                      <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginTop: '0.15rem' }}>
                        {subtitle}
                      </div>
                    </div>
                  </div>

                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '1.15rem', fontWeight: 800, color: isSelected ? '#00e5ff' : 'white' }}>
                      {priceStr}
                    </div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                      {isAnnual ? '/ año' : '/ mes'}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Status Messages */}
        {errorMessage && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '10px', padding: '0.65rem 0.9rem', width: '100%', marginBottom: '1rem',
            color: '#ef4444', fontSize: '0.82rem', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '0.5rem'
          }}>
            <AlertCircle size={16} style={{ flexShrink: 0 }} />
            <span>{errorMessage}</span>
          </div>
        )}

        {successMessage && (
          <div style={{
            background: 'rgba(16, 185, 129, 0.15)', border: '1px solid rgba(16, 185, 129, 0.3)',
            borderRadius: '10px', padding: '0.65rem 0.9rem', width: '100%', marginBottom: '1rem',
            color: '#10b981', fontSize: '0.85rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem'
          }}>
            <Check size={16} style={{ flexShrink: 0 }} />
            <span>{successMessage}</span>
          </div>
        )}

        {/* CTA Purchase Button */}
        <button
          onClick={handlePurchase}
          disabled={purchasing || restoring || loadingOfferings}
          style={{
            background: 'linear-gradient(135deg, #00e5ff, #8b5cf6)',
            color: '#050810', border: 'none', padding: '0.95rem 1.75rem',
            borderRadius: '14px', fontSize: '1.02rem', fontWeight: 800, width: '100%',
            cursor: (purchasing || restoring || loadingOfferings) ? 'not-allowed' : 'pointer',
            opacity: (purchasing || restoring || loadingOfferings) ? 0.7 : 1,
            transition: 'all 0.2s', marginBottom: '0.85rem',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
            boxShadow: '0 6px 20px rgba(0, 229, 255, 0.35)'
          }}
          onMouseOver={e => {
            if (!purchasing && !restoring) e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseOut={e => {
            if (!purchasing && !restoring) e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          {purchasing ? (
            <>
              <RefreshCw size={18} className="spin-animation" />
              Procesando con App Store...
            </>
          ) : (
            `Continuar con NousMetrics PRO`
          )}
        </button>

        {/* Restore Purchases Button (Mandatory for Apple Guideline 3.1.2) */}
        <button
          onClick={handleRestore}
          disabled={purchasing || restoring}
          style={{
            background: 'transparent', border: 'none',
            color: 'var(--text-secondary)', fontSize: '0.82rem', fontWeight: 600,
            cursor: (purchasing || restoring) ? 'not-allowed' : 'pointer',
            padding: '0.4rem 0.8rem', borderRadius: '8px',
            transition: 'color 0.2s', marginBottom: '1rem',
            display: 'inline-flex', alignItems: 'center', gap: '0.4rem'
          }}
          onMouseOver={e => e.currentTarget.style.color = '#00e5ff'}
          onMouseOut={e => e.currentTarget.style.color = 'var(--text-secondary)'}
        >
          {restoring ? (
            <>
              <RefreshCw size={14} className="spin-animation" />
              Restaurando compras...
            </>
          ) : (
            'Restaurar Compras Anteriores'
          )}
        </button>

        {/* Apple Required Auto-Renewable Terms & Disclosure */}
        <div style={{
          borderTop: '1px solid rgba(255, 255, 255, 0.08)',
          paddingTop: '0.9rem', width: '100%',
          fontSize: '0.72rem', color: 'rgba(255, 255, 255, 0.55)',
          lineHeight: '1.45', textAlign: 'justify'
        }}>
          <p style={{ margin: '0 0 0.5rem 0' }}>
            El pago se cargará a tu cuenta de Apple ID / Google Play al confirmar la compra. La suscripción se renueva automáticamente a menos que se cancele al menos 24 horas antes del final del período actual. Puedes gestionar o cancelar tus suscripciones en cualquier momento desde los Ajustes de tu cuenta tras la compra.
          </p>

          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: '1.25rem', marginTop: '0.6rem', fontSize: '0.75rem'
          }}>
            <button
              onClick={() => handleOpenLink('/terms.html')}
              style={{
                background: 'none', border: 'none', padding: 0,
                color: '#00e5ff', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                textDecoration: 'underline'
              }}
            >
              Condiciones de Uso <ExternalLink size={11} />
            </button>
            <span style={{ color: 'rgba(255, 255, 255, 0.2)' }}>•</span>
            <button
              onClick={() => handleOpenLink('/privacy.html')}
              style={{
                background: 'none', border: 'none', padding: 0,
                color: '#00e5ff', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                textDecoration: 'underline'
              }}
            >
              Política de Privacidad <ExternalLink size={11} />
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}

function FeatureCard({ icon, title, desc }) {
  return (
    <div style={{
      background: 'rgba(255, 255, 255, 0.02)',
      border: '1px solid rgba(255, 255, 255, 0.06)',
      borderRadius: '12px', padding: '0.75rem',
      display: 'flex', flexDirection: 'column', gap: '0.25rem'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem' }}>
        {icon}
        <span style={{ color: 'white', fontSize: '0.82rem', fontWeight: 700 }}>{title}</span>
      </div>
      <p style={{ color: 'var(--text-secondary)', fontSize: '0.72rem', margin: 0, lineHeight: 1.35 }}>
        {desc}
      </p>
    </div>
  );
}
